/**
 * EditablePillRow (VOY-472) — inline-editable structured-fields row.
 *
 * Five pills (FROM, TO, DATES, PAX, CABIN) that each open a popover with
 * the appropriate picker. All state reads and writes go through the
 * `useSearchState` hook (VOY-471) passed in as a prop — no local pill
 * state. Closing a popover commits the change immediately; there is no
 * separate Save/Apply.
 *
 * Visual chrome is provided by the `.sc-pill*` and `.sc-picker*` rules
 * in `src/styles/voy-v6.css`, all scoped under `.voy-v6`. This component
 * **must be rendered inside a `.voy-v6` ancestor** (the LandingV6 shell
 * already provides this; the temporary preview route at
 * `/internal/pill-row-preview` wraps the page in `voy-v6` explicitly).
 * Without that scope the pills render unstyled.
 *
 * Currently unmounted in any production route. VOY-473's SmartSearchCard
 * renders it in place of the existing `sc-compare-wrap` block in
 * HeroSearchCard.
 */

import { ArrowRightLeft, Calendar as CalendarIcon, ChevronDown, MapPin, Users } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { UseSearchStateApi } from '@/lib/search/useSearchState'

import { AirportPicker } from './pickers/AirportPicker'
import { DateRangePicker } from './pickers/DateRangePicker'
import { PassengerPicker } from './pickers/PassengerPicker'
import { CabinPicker } from './pickers/CabinPicker'
import { formatAirport, formatCabinLabel, formatDateRange, formatPassengers } from './format'

interface EditablePillRowProps {
  searchState: UseSearchStateApi
  className?: string
}

export function EditablePillRow({ searchState, className }: EditablePillRowProps) {
  const { state, setField, setMany } = searchState

  const swapAirports = () => {
    setMany({ from: state.to, to: state.from })
  }

  const showDates = state.tripType !== 'multi-city'

  return (
    <div className={`sc-pill-row${className ? ` ${className}` : ''}`} data-pill-row>
      <span className="sc-pill-edit">Edit</span>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="sc-pill"
            data-pill="from"
            data-empty={!state.from?.code}
            aria-label="Edit origin"
          >
            <MapPin className="sc-pill-icon" aria-hidden="true" />
            <span className="sc-pill-label">From</span>
            <span className="sc-pill-value">{formatAirport(state.from)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <AirportPicker
            value={state.from}
            onChange={(next) => setField('from', next)}
          />
        </PopoverContent>
      </Popover>

      <SwapButton
        onSwap={swapAirports}
        disabled={!state.from?.code && !state.to?.code}
      />

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="sc-pill"
            data-pill="to"
            data-empty={!state.to?.code}
            aria-label="Edit destination"
          >
            <MapPin className="sc-pill-icon" aria-hidden="true" />
            <span className="sc-pill-label">To</span>
            <span className="sc-pill-value">{formatAirport(state.to)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <AirportPicker
            value={state.to}
            onChange={(next) => setField('to', next)}
          />
        </PopoverContent>
      </Popover>

      {showDates ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="sc-pill"
              data-pill="dates"
              data-empty={!state.departureDate && !state.returnDate}
              aria-label="Edit travel dates"
            >
              <CalendarIcon className="sc-pill-icon" aria-hidden="true" />
              <span className="sc-pill-label">Dates</span>
              <span className="sc-pill-value">
                {formatDateRange(state.departureDate, state.returnDate, state.tripType)}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-3">
            <DateRangePicker
              departureDate={state.departureDate}
              returnDate={state.returnDate}
              tripType={state.tripType}
              onChange={(next) => setMany(next)}
            />
          </PopoverContent>
        </Popover>
      ) : null}

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="sc-pill"
            data-pill="pax"
            aria-label="Edit travelers"
          >
            <Users className="sc-pill-icon" aria-hidden="true" />
            <span className="sc-pill-label">Pax</span>
            <span className="sc-pill-value">{formatPassengers(state.passengers)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <PassengerPicker
            value={state.passengers}
            onChange={(next) => setField('passengers', next)}
          />
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="sc-pill"
            data-pill="cabin"
            aria-label="Edit cabin"
          >
            <span className="sc-pill-label">Cabin</span>
            <span className="sc-pill-value">{formatCabinLabel(state.cabin)}</span>
            <ChevronDown className="sc-pill-icon" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <CabinPicker
            value={state.cabin}
            onChange={(next) => setField('cabin', next)}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface SwapButtonProps {
  onSwap: () => void
  disabled?: boolean
}

function SwapButton({ onSwap, disabled }: SwapButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label="Swap origin and destination"
      className="sc-pill-swap"
      onClick={onSwap}
    >
      <ArrowRightLeft aria-hidden="true" />
    </button>
  )
}

